import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { StateService, TransitionOptions } from '@uirouter/core';
import { ControlService } from './control.service';
import { CurrentControlService } from '../shared/services/current-control.service';
import { CurrentDeviceModelsService } from '../shared/services/current-device-models.service';
import { DeviceModelFactoryService } from '../shared/services/device-model-factory.service';
import { IControlDeviceModel, IControlModel, IControlSubRoute, IDeviceModel } from '../shared/models/models';
import { controls } from '../shared/models/device-routes.enum';

@Component({
    selector: 'dl-controls',
    templateUrl: './control.component.html',
    styleUrls: ['./control.component.scss']
})
export class ControlComponent implements OnChanges, OnInit {
    public static $name: string = 'controls';

    public areaName: string;

    public isReady: boolean = false;
    public sections: string[] = [];

    public currentControl: IControlModel;

    @Input() public areaId: number;
    @Input() public locationId: number;
    @Input() public hardwareItemId: string;
    @Input() public modelName: string;

    deviceModels: { [key: string]: IDeviceModel } = {};

    constructor(
        private $state: StateService,
        private controlService: ControlService,
        private currentControlService: CurrentControlService,
        private currentDeviceModelsService: CurrentDeviceModelsService,
        private deviceModelFactory: DeviceModelFactoryService
    ) {
    }

    public get currentHardwareId() {
        if (this.currentControl) {
            return this.currentControl.id;
        }

        return null;
    }

    public ngOnChanges() {
        this.setCurrentControlFromId(this.hardwareItemId);
    }

    public ngOnInit() {
        this.setup();
    }

    public visibleSections() {
        return this.sections.filter(section => {
            return this.deviceModels[section] && this.deviceModels[section].controls.length;
        });
    }

    setCurrentControl(section: string, control: IControlModel) {
        // make sure everything still works if, for any reason,
        // the user has a bookmark to a non-lowercase url (old versions used camelCase for modelName)
        let modelName = this.modelName && this.modelName.toLowerCase();
        let hardwareItemId = parseInt(this.hardwareItemId, 10);

        this.currentControl = control;
        this.currentControlService.setCurrentControl(this.currentControl);

        if (hardwareItemId && modelName && this.currentControl.id !== hardwareItemId) {
            const to: IControlSubRoute = this.getRoute(modelName, section, this.$state.current.name);

            // If we were on a visible subroute on a card, and attempt to select another card
            // and that equivalent route is not active for that control, we should go back to the base controls state
            if (to.isVisible(control)) {
                const params = {
                    hardwareItemId: this.currentControl.id.toString(),
                    modelName: section,
                    areaId: this.areaId,
                    locationId: this.locationId,
                };

                const options: TransitionOptions = {
                    reload: to.name
                };

                this.$state.go(to.name, params, options);
            } else {
                this.$state.go(controls.controlsDetails);
            }
        }
    }

    /**
     * Since we have different routes for different models,
     * we have to have a way to line them up so that selecting a different card
     * can take you to the separate-but-equivalent route for the other model
     */
    private getRoute(fromSection: string, toSection: string, currentStateName: string): IControlSubRoute {
        let fromModel: IDeviceModel = this.deviceModels[fromSection];
        let toModel: IDeviceModel = this.deviceModels[toSection];

        // Find the key from the from state
        let key: string;

        for (let subrouteKey in fromModel.subroutes) {
            let currentSubroute: IControlSubRoute = fromModel.subroutes[subrouteKey];

            if (currentSubroute.name === currentStateName) {
                key = subrouteKey;
                break;
            }
        }

        // Use the key to return the corresponding to state
        return toModel.subroutes[key];
    }

    // This can be optimized later by storing the controls as a dictionary instead of an array
    private setCurrentControlFromId(hardwareItemId: string) {
        if (!hardwareItemId) {
            return;
        }

        for (let section in this.sections) {
            let currentSection = this.deviceModels[this.sections[section]];

            for (let control in currentSection.controls) {
                let currentControl = currentSection.controls[control];

                if (currentControl.id === parseInt(hardwareItemId, 10)) {
                    this.setCurrentControl(section, currentControl);
                    return;
                }
            }
        }
    }

    private setup() {
        this.controlService.getControlsForArea(this.locationId, this.areaId)
            .then(result => {
                this.areaName = result.areaName;
                this.sections = Object.keys(result.controls);
                const len = this.sections.length;

                for (let i = 0; i < len; i++) {
                    const key = this.sections[i];
                    const data = result.controls[key] as IControlDeviceModel;
                    this.deviceModels[key] = this.deviceModelFactory.createDynamic(key, data, this.areaId, this.locationId);
                }

                this.currentDeviceModelsService.setCurrentDeviceModels(this.deviceModels);
                this.setCurrentControlFromId(this.hardwareItemId);

                this.isReady = true;
            });
    }
}
